import { useState, memo, Fragment, useMemo } from 'react';
import { Accordion, Tab, Tabs } from 'react-bootstrap';
import AccordionSubmissionModal from '../../../../Admin/Tasks/AccordionSubmissionModal';
import { FaWpforms } from 'react-icons/fa';
import { css } from 'twin.macro';
import { HiOutlineCheckCircle, HiOutlineExclamationCircle } from 'react-icons/hi';
import OurAccordionMultiple from '../../../../../_elements/OurAccordionMultiple';
import StaticPreview from '../../../../Admin/Form/StaticPreview';
import MessageTime from '../../../Common/MessageTime';
import { isAllowedDraftFormType } from '@/components/Admin/Form/ViewForm';
import { getUserRole } from '@/_helpers';
import { RiDraftLine } from 'react-icons/ri';
import PdfTemplatePreview from '@/components/Admin/Form/PdfTemplatePreview';

const FormTitleDiv = memo(({ form, userFormData }) => {
    const submittedAt = userFormData?.createdAt ? new Date(userFormData?.createdAt || 0)?.toString() : '';
    const submissionIcons = {
        draft: <RiDraftLine size={30} className="text-warning cursor-pointer ml-2" />,
        completed: <HiOutlineCheckCircle size={30} className="text-primary cursor-pointer ml-2" />,
        default: <HiOutlineExclamationCircle size={30} className="text-secondary cursor-pointer ml-2" />,
    };
    const SubmissionIcon = submissionIcons[userFormData?.submissionType] || submissionIcons.default;

    return (
        <div
            className="pointer d-flex"
            css={css`
                align-items: center;
                gap: 6px;
                font-size: 16px;
                flex-direction: row;
                justify-content: space-between;
                width: 100%;
            `}
        >
            <div
                className="d-flex"
                css={css`
                    align-items: center;
                    gap: 6px;
                `}
            >
                <FaWpforms />
                <span>{form.name}</span>
                <span className="badge badge-info">
                    {
                        {
                            eos: 'end of service',
                            sos: 'start of service',
                            multipleSubmission: 'multiple submission',
                            normal: 'normal',
                            speciality: 'speciality',
                        }[form.type]
                    }
                </span>
            </div>
            <div
                className="d-flex"
                css={css`
                    align-items: center;
                    gap: 6px;
                `}
            >
                <span>{SubmissionIcon}</span>
                {userFormData?.submissionType ? (
                    <span className="px-2 text-right text-muted text-uppercase">
                        <MessageTime dateAndTime={submittedAt} />{' '}
                    </span>
                ) : null}
            </div>
        </div>
    );
});

const FormGroupAccordionForms = memo(
    ({
        forms = [],
        userId,
        currRoom,
        currUser,
        team,
        position,
        checkIsShowing,
        handleCloseAccordionModal = () => {},
        formGroupId,
        setActiveFormInMarker = () => {},
        formGroupData = {},
        preview,
    }) => {
        const [activeKey, setActiveKey] = useState('9e9');
        const [key, setKey] = useState('staticform');
        const pdfTemplate = useMemo(() => {
            return formGroupData?.pdfTemplate || {};
        }, [formGroupData]);
        const pdfMappings = useMemo(() => {
            return formGroupData?.pdfMappings || {};
        }, [formGroupData]);
        const combinedFormElements = useMemo(() => {
            return forms
                ?.reduce((acc, form) => {
                    const formElements = form?.formElements || [];
                    return [...acc, ...formElements];
                }, [])
                .map((element) => {
                    const isRadioOrCheckbox = element?.key === 'Radio' || element?.key === 'Checkbox';
                    const elementId = element?._id || element?.id;
                    let hasChildWithConfig = false;

                    if (isRadioOrCheckbox) {
                        const updatedData = (element?.data || [])?.map((option) => {
                            const optionKey = option?.value || option?._id || option?.id;
                            const optionConfig = pdfMappings[optionKey];

                            if (optionConfig) {
                                hasChildWithConfig = true;
                                return {
                                    ...option,
                                    config: {
                                        ...optionConfig,
                                        parentFormElement: {
                                            id: elementId,
                                            key: element.key,
                                        },
                                    },
                                };
                            }
                            return option;
                        });

                        return {
                            ...element,
                            data: updatedData,
                            config: {
                                ...(pdfMappings[elementId] || {}),
                                hasTemplateConfig: hasChildWithConfig || !!pdfMappings[elementId]?.rectConfig,
                            },
                        };
                    }

                    return {
                        ...element,
                        config: pdfMappings[elementId] || {},
                    };
                });
        }, [forms]);
        return (
            <Fragment>
                <Tabs
                    activeKey={key}
                    onSelect={(k) => setKey(k)}
                    className="mb-3"
                    css={css`
                        .active {
                            .tab-title {
                                color: #495057 !important;
                            }
                        }
                    `}
                >
                    <Tab eventKey="staticform" title={<span className="tab-title">Static Form</span>}>
                        {key === 'staticform' && (
                            <div
                                css={css`
                                    border: 2px solid #50aeb0;
                                `}
                            >
                                <Accordion
                                    defaultActiveKey={activeKey}
                                    onSelect={(e) => {
                                        setActiveKey(e);
                                        if ((forms || [])?.length >= e) {
                                            setActiveFormInMarker(forms[e]?._id);
                                        }
                                    }}
                                >
                                    {forms?.map((_form, formIndex) => {
                                        const form = _form?.form || {};
                                        const userFormData = _form?.userFormData || {};
                                        const formElements = _form?.formElements || [];
                                        const isDraft =
                                            isAllowedDraftFormType(userFormData) &&
                                            userFormData?.submissionType === 'draft';
                                        const canComplete =
                                            ['admin', 'owner', 'teamManager', 'supervisor'].includes(
                                                getUserRole(currUser, userFormData?.team, userFormData?.chatroom)
                                            ) ||
                                            userFormData?.user === userId ||
                                            (userFormData?.visibleTo || []).includes('user');
                                        const canSubmit = canComplete;
                                        return (
                                            <OurAccordionMultiple
                                                title={<FormTitleDiv form={form} userFormData={userFormData} />}
                                                eventKey={formIndex.toString()}
                                                key={form?._id || form?.id || formIndex}
                                                isOpen={activeKey === formIndex.toString()}
                                                titleTag=""
                                            >
                                                {activeKey === formIndex.toString() && (
                                                    <div
                                                        css={css`
                                                            padding: 2px;
                                                        `}
                                                    >
                                                        {userFormData?._id ? (
                                                            <StaticPreview
                                                                formElements={formElements}
                                                                isPreview={!isDraft}
                                                                userFormData={userFormData || {}}
                                                                handleCloseModal={handleCloseAccordionModal}
                                                                isDraft={isDraft}
                                                                canSubmit={canSubmit}
                                                                canComplete={canComplete}
                                                                formGroupId={formGroupId}
                                                            />
                                                        ) : (
                                                            <AccordionSubmissionModal
                                                                key={form?._id || form?.id || formIndex}
                                                                form={form}
                                                                roomId={currRoom && (currRoom?.id || currRoom?._id)}
                                                                userId={userId}
                                                                currRoom={currRoom}
                                                                formType={form?.type}
                                                                team={team}
                                                                position={position}
                                                                checkIsShowing={checkIsShowing}
                                                                activeKey={activeKey}
                                                                isAccordionForm={true}
                                                                handleClose={handleCloseAccordionModal}
                                                                formGroupId={formGroupId}
                                                            />
                                                        )}
                                                        <hr />
                                                    </div>
                                                )}
                                            </OurAccordionMultiple>
                                        );
                                    })}
                                </Accordion>
                            </div>
                        )}
                    </Tab>
                    {pdfTemplate?.url && preview && (
                        <Tab eventKey="teamplatepdf" title={<span className="tab-title">Template PDF</span>}>
                            {key === 'teamplatepdf' && (
                                <div
                                    css={css`
                                        height: 70vh;
                                        overflow: auto;
                                    `}
                                >
                                    <PdfTemplatePreview
                                        width="100%"
                                        height="100%"
                                        css={css`
                                            border: none;
                                            outline: none;
                                        `}
                                        formTemplateUrl={pdfTemplate?.url}
                                        formElements={combinedFormElements}
                                        formName={formGroupData?.name || `form-group-submission`}
                                        pdfDimension={pdfTemplate?.metadata?.dimensions || {}}
                                        showDownloadButton={true}
                                    ></PdfTemplatePreview>
                                </div>
                            )}
                        </Tab>
                    )}
                </Tabs>
            </Fragment>
        );
    }
);
export default FormGroupAccordionForms;
